// 2026-08 — "How to Install" guide: step-by-step PWA install + notification
// instructions for iPhone and Android, forwardable to the team as-is.
import { useState } from 'react';
import { Smartphone, Share, PlusSquare, Bell, CheckCircle2, Send, Loader2, Chrome, Apple } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePushNotifications, isIOS, isStandalone, pushSupported } from '../lib/usePushNotifications';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { pushAPI } from '../lib/api';
import { toast } from 'sonner';

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="h-6 w-6 rounded-full bg-gold/15 text-gold text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
    <span className="text-[13px] text-foreground/85 leading-relaxed">{children}</span>
  </li>
);

export default function InstallGuidePage() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { subscribe } = usePushNotifications({ userId: user?.id, workspaceId: currentWorkspace?.id, autoEnroll: false });
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const perm = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
  const iosNotInstalled = isIOS() && !isStandalone();

  const enableNotifications = async () => {
    if (iosNotInstalled) {
      toast.info('Install the app first (steps above), then open it from your Home Screen and tap this button again.');
      return;
    }
    if (!pushSupported()) {
      toast.error('This browser does not support push notifications.');
      return;
    }
    setBusy(true);
    const ok = await subscribe();
    setBusy(false);
    if (ok) toast.success('Notifications enabled on this device!');
    else if (Notification.permission === 'denied') toast.error('Notifications are blocked — allow them in your browser/site settings.');
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const res = await pushAPI.sendTest(currentWorkspace?.id);
      const n = res.data?.sent_to ?? 0;
      if (n > 0) toast.success(`Test notification sent to ${n} device${n === 1 ? '' : 's'} — check your notification tray.`);
      else toast.info('No subscribed devices yet — enable notifications first.');
    } catch {
      toast.error('Could not send test notification.');
    }
    setTestBusy(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-24 space-y-6" data-testid="install-guide-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-display font-bold flex items-center gap-3">
          <Smartphone className="h-8 w-8 text-gold" /> Install on your phone
        </h1>
        <p className="text-base text-muted-foreground/70 mt-2">
          Get Stellar CRM on your home screen — it opens full-screen like a normal app and sends task notifications straight to your phone.
        </p>
      </div>

      {/* iPhone */}
      <div className="rounded-2xl border border-border/30 bg-card p-5 space-y-4" data-testid="install-guide-ios">
        <h2 className="text-lg font-display font-semibold flex items-center gap-2"><Apple className="h-5 w-5" /> iPhone (Safari)</h2>
        <ol className="space-y-3">
          <Step n={1}>Open this CRM link in <strong>Safari</strong> (it must be Safari, not Chrome or the Gmail/WhatsApp in-app browser).</Step>
          <Step n={2}>Tap the <strong>Share</strong> button <Share className="h-3.5 w-3.5 inline text-gold" /> at the bottom of the screen.</Step>
          <Step n={3}>Scroll down and tap <strong>"Add to Home Screen"</strong> <PlusSquare className="h-3.5 w-3.5 inline text-gold" />, then tap <strong>Add</strong>.</Step>
          <Step n={4}>Close Safari and open the new <strong>Stellar CRM</strong> icon from your home screen. It opens full-screen with no browser bar.</Step>
          <Step n={5}>Log in, then when the gold banner asks, tap <strong>Enable</strong> and choose <strong>Allow</strong> to turn on notifications. <span className="text-muted-foreground/60">(Needs iOS 16.4 or newer. Notifications only work from the installed app, not from Safari.)</span></Step>
        </ol>
      </div>

      {/* Android */}
      <div className="rounded-2xl border border-border/30 bg-card p-5 space-y-4" data-testid="install-guide-android">
        <h2 className="text-lg font-display font-semibold flex items-center gap-2"><Chrome className="h-5 w-5" /> Android (Chrome)</h2>
        <ol className="space-y-3">
          <Step n={1}>Open this CRM link in <strong>Chrome</strong>.</Step>
          <Step n={2}>Tap the <strong>⋮ menu</strong> (top-right), then tap <strong>"Add to Home screen"</strong> (or <strong>"Install app"</strong> if shown), and confirm.</Step>
          <Step n={3}>Open the new <strong>Stellar CRM</strong> icon from your home screen — it opens full-screen like a native app.</Step>
          <Step n={4}>Log in, then when the gold banner asks, tap <strong>Enable</strong> and choose <strong>Allow</strong> to turn on notifications.</Step>
        </ol>
      </div>

      {/* What it looks like */}
      <div className="rounded-2xl border border-border/30 bg-card p-5 space-y-3" data-testid="install-guide-result">
        <h2 className="text-lg font-display font-semibold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Once installed, you'll have</h2>
        <ul className="space-y-2 text-[13px] text-foreground/85">
          <li className="flex gap-2 items-start"><span className="text-gold">•</span> A gold <strong>Stellar CRM</strong> icon on your home screen.</li>
          <li className="flex gap-2 items-start"><span className="text-gold">•</span> The app opens full-screen — no address bar, no browser buttons.</li>
          <li className="flex gap-2 items-start"><span className="text-gold">•</span> Task updates, reviews, and comments arrive on your lock screen like any other app notification.</li>
        </ul>
      </div>

      {/* Notification controls */}
      <div className="rounded-2xl border border-gold/25 bg-gold/[0.04] p-5 space-y-3" data-testid="install-guide-notifications">
        <h2 className="text-lg font-display font-semibold flex items-center gap-2"><Bell className="h-5 w-5 text-gold" /> Notifications on this device</h2>
        <p className="text-[12px] text-muted-foreground/70">
          Status: <strong className="text-foreground">{perm === 'granted' ? 'Enabled' : perm === 'denied' ? 'Blocked in browser settings' : perm === 'default' ? 'Not enabled yet' : 'Not supported'}</strong>
          {iosNotInstalled && ' — install the app first (steps above), then enable from inside the installed app.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={enableNotifications} disabled={busy} className="h-10 bg-gold/15 hover:bg-gold/25 text-gold border border-gold/30" data-testid="install-guide-enable-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />} Enable notifications
          </Button>
          <Button onClick={sendTest} disabled={testBusy} variant="outline" className="h-10 border-border/30" data-testid="install-guide-test-btn">
            {testBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send test notification
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground/50">
          To turn notifications off later, disable them for Stellar CRM in your phone's notification settings — we stop sending immediately.
        </p>
      </div>
    </div>
  );
}
